import React, { useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function GuardianConsentBanner() {
  const { user, refreshMe } = useAuth();
  const [guardianEmail, setGuardianEmail] = useState(user?.guardian_email || "");
  const [submitting, setSubmitting] = useState(false);
  const [link, setLink] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  if (!user || !user.is_minor) return null;
  if (user.guardian_consent_status === "granted") {
    return <GuardianGrantedBanner user={user} />;
  }

  const request = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/auth/request-guardian-consent", {
        guardian_email: guardianEmail,
      });
      setLink(data.consent_link);
      await refreshMe();
    } catch (err) {
      setError(extractError(err, "Could not send consent request"));
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn("Could not copy consent link:", err);
    }
  };

  return (
    <div
      className="rounded-2xl bg-[color:var(--surface-sage)] border border-[color:var(--line)] p-5 mb-6"
      data-testid="guardian-consent-banner"
    >
      <div className="flex items-start gap-3">
        <div className="mt-1 w-8 h-8 rounded-full bg-white border border-[color:var(--line)] flex items-center justify-center text-[color:var(--participant-a)]">
          ♥
        </div>
        <div className="flex-1">
          <div className="font-serif text-lg text-[color:var(--text-primary)]">
            Guardian consent (Hybrid policy)
          </div>
          <p className="text-sm text-[color:var(--text-muted)] mt-1">
            You're aged 13–17. You can use the Free tier without consent. To
            upgrade your plan or involve a professional mediator, please send
            this consent link to a parent or guardian.
          </p>

          {!link && (
            <form onSubmit={request} className="mt-3 flex flex-wrap items-center gap-2" data-testid="guardian-request-form">
              <input
                type="email"
                required
                placeholder="Guardian email (e.g. parent@example.com)"
                value={guardianEmail}
                onChange={(e) => setGuardianEmail(e.target.value)}
                className="px-3 py-2 rounded-full border border-[color:var(--line)] bg-white text-sm min-w-[280px]"
                data-testid="guardian-email-input"
              />
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 rounded-full bg-[color:var(--participant-a)] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                data-testid="guardian-request-submit"
              >
                {submitting ? "Sending…" : "Send consent link"}
              </button>
            </form>
          )}

          {link && (
            <div className="mt-3 space-y-2" data-testid="guardian-link-block">
              <p className="text-xs text-[color:var(--text-muted)]">
                Share this link with your guardian. (Email transport is not yet
                wired — copy and send it.)
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <code className="text-xs bg-white border border-[color:var(--line)] rounded-lg px-3 py-2 break-all flex-1 min-w-[260px]">
                  {link}
                </code>
                <button
                  type="button"
                  onClick={copyLink}
                  className="px-3 py-2 rounded-full bg-white border border-[color:var(--line)] text-xs font-medium hover:bg-[color:var(--bg-base)]"
                  data-testid="guardian-link-copy"
                >
                  {copied ? "Copied!" : "Copy link"}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-2 text-sm text-[color:var(--counter-error)]" data-testid="guardian-request-error">
              {error}
            </div>
          )}

          <div className="mt-3 text-xs text-[color:var(--text-muted)]">
            Status:{" "}
            <span
              className={`inline-block px-2 py-0.5 rounded-full ${
                user.guardian_consent_status === "pending"
                  ? "bg-yellow-50 text-yellow-800 border border-yellow-200"
                  : "bg-white border border-[color:var(--line)]"
              }`}
              data-testid="guardian-consent-status"
            >
              {user.guardian_consent_status || "none"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}


function GuardianGrantedBanner({ user }) {
  const dashboardUrl = user.guardian_token
    ? `${window.location.origin}/guardian/${user.guardian_token}`
    : null;
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    if (!dashboardUrl) return;
    try {
      await navigator.clipboard.writeText(dashboardUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn("Could not copy guardian link:", err);
    }
  };
  return (
    <div
      className="rounded-2xl bg-[color:var(--surface-sage)] border border-[color:var(--line)] p-4 mb-6 flex items-start gap-3"
      data-testid="guardian-granted-banner"
    >
      <div className="mt-1 w-8 h-8 rounded-full bg-white border border-[color:var(--line)] flex items-center justify-center text-green-700">
        ✓
      </div>
      <div className="flex-1 text-sm">
        <div className="font-medium text-[color:var(--text-primary)]">
          Guardian consent active
        </div>
        <p className="text-[color:var(--text-muted)] mt-1">
          {user.guardian_name || "Your guardian"} has approved your activity.
          They can view a transparent log of everything you do here and revoke
          consent at any time. Be open with them — the link below is private to
          them.
        </p>
        {dashboardUrl && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <code className="text-[11px] bg-white border border-[color:var(--line)] rounded-lg px-2 py-1 break-all flex-1 min-w-[260px]">
              {dashboardUrl}
            </code>
            <button
              type="button"
              onClick={copy}
              className="px-3 py-1.5 rounded-full bg-white border border-[color:var(--line)] text-xs font-medium hover:bg-white/70"
              data-testid="guardian-link-copy-granted"
            >
              {copied ? "Copied!" : "Copy for your guardian"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
